import { Collapse, Group, Paper, Text, Tooltip, ActionIcon } from "@mantine/core";
import {
  IconBell,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconPower,
  IconRadar,
  IconRotate,
  IconShield,
} from "@tabler/icons-react";
import { useState } from "react";
import ComputerCard from "./ComputerCard";
import { openConfirmModal } from "@mantine/modals";
import { modalTitle } from "../modals/Modal";
import { useFetch } from "../../helpers/useFetch";
import { toast } from "react-hot-toast";
import { openPcAlertModal, openPcBlockModal } from "../../helpers/modals";

function isPcOnline(computer) {
  if (!computer?.last_seen_at) return false;
  return Date.now() - new Date(computer.last_seen_at).getTime() < 6 * 60 * 1000;
}

async function sendToAll(computers, type, payload = null) {
  const body = payload ? { type, payload } : { type };
  await Promise.all(
    computers.filter(isPcOnline).map((c) => useFetch(`/pcapp/room/computer/${c.id}/command`, "POST", body))
  );
}

export default function RoomComputers({ room, onRefresh }) {
  const [opened, setOpened] = useState(false);

  const turnOffAll = () => {
    openConfirmModal({
      title: modalTitle(IconPower, "Vypnutí všech počítačů"),
      children: (
        <Text size="sm">
          Opravdu chceš vypnout všechny počítače? Tento krok prováděj pouze na konci programu případně v nouzi.
        </Text>
      ),
      labels: { confirm: "Potvrdit", cancel: "Zrušit" },
      onConfirm: async () => {
        await sendToAll(room.computers, "shutdownStart");
        toast.success("Počítače se vypnou za minutu.");
        onRefresh();
      },
    });
  };

  const rebootAll = () => {
    openConfirmModal({
      title: modalTitle(IconRotate, "Restart všech počítačů"),
      children: <Text size="sm">Opravdu chceš restartovat všechny počítače? Tento krok prováděj pouze v nouzi.</Text>,
      labels: { confirm: "Potvrdit", cancel: "Zrušit" },
      onConfirm: async () => {
        await sendToAll(room.computers, "rebootStart");
        toast.success("Počítače se restartují za minutu.");
        onRefresh();
      },
    });
  };

  const blockAll = () =>
    openPcBlockModal(async (durationSeconds) => {
      await sendToAll(room.computers, "block", durationSeconds ? { durationSeconds } : null);
      toast.success("Počítače zablokovány.");
      onRefresh();
    }, room.name);

  const pingAll = async () => {
    await sendToAll(room.computers, "ping");
    toast.success("Ping na počítače odeslán.");
    onRefresh();
  };

  const openAlert = () =>
    openPcAlertModal(async ({ title, message }) => {
      await sendToAll(room.computers, "alert", { title, message });
      toast.success("Alert odeslán na místnost.");
      onRefresh();
    }, room.name);

  const checkForUpdates = async () => {
    await sendToAll(room.computers, "checkForUpdates");
    toast.success("Kontrola aktualizací spuštěna.");
    onRefresh();
  };

  return (
    <>
      <Paper p="sm" className="mb-4 cursor-pointer" withBorder radius="md" onClick={() => setOpened(!opened)}>
        <Group justify="space-between">
          <h2>{room.name}</h2>
          <div className="flex items-center gap-4">
            <Group gap="sm">
              <Tooltip label="Vypnout místnost" position="top" withArrow>
                <ActionIcon
                  variant="light"
                  color="red"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    turnOffAll();
                  }}>
                  <IconPower size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Restartovat místnost" position="top" withArrow>
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    rebootAll();
                  }}>
                  <IconRotate size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Zablokovat místnost" position="top" withArrow>
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    blockAll();
                  }}>
                  <IconShield size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Ping místnosti" position="top" withArrow>
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    pingAll();
                  }}>
                  <IconRadar size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Odeslat alert místnosti" position="top" withArrow>
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAlert();
                  }}>
                  <IconBell size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Aktualizovat manager místnosti" position="top" withArrow>
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    checkForUpdates();
                  }}>
                  <IconDownload size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Group>
            {opened ? <IconChevronUp stroke={1.5} /> : <IconChevronDown stroke={1.5} />}
          </div>
        </Group>
      </Paper>
      <Collapse in={opened}>
        <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          {room.computers.length === 0 && (
            <Text c="dimmed" className="col-span-full text-center text-lg">
              😢 Žádné počítače
            </Text>
          )}
          {room.computers.map((computer) => (
            <ComputerCard key={computer.id} computer={computer} onRefresh={onRefresh} />
          ))}
        </div>
      </Collapse>
    </>
  );
}
